import useProperties from "../../hooks/use-properties";
import { useState } from "react";
import { currencyFormatter } from "../../api/Functions";
import { ReactSelectStandalone } from "../UI/SelectStandalone";
import useStripePrices, {
  useStripeComptaPrice,
} from "../../hooks/use-stripe-prices";
import { ProductType } from "../../utils/constants/stripe";
import usePropertyFolders from "../../hooks/use-property-folders";
import { BalanceDomain } from "../../utils/constants/balances";
import { formatBalanceAffectation } from "../../utils/balances";
import Button from "../UI/Button";
import { PropertyType } from "../../models/property";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import { ModalDialogFooter } from "../UI/ModalDialog";
import Text from "../UI/Text";
import { TextOneOrMany } from "../../utils/index.jsx";
import { Check } from "../UI/Icons";
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import { COMPTA_PRICE } from "../../utils/constants/compta";
import { useModal } from "../../hooks/use-modal.jsx";

import useStripePayment from "../../hooks/use-stripe-payment.js";

export default function BuyComptabiliteForm({
  Modal,
  fiscalYear,
  onPaymentSuccessful,
}) {
  const { properties, propertiesById } = useProperties({ withById: true });
  const { propertyFolders, propertyFoldersById } = usePropertyFolders({
    withById: true,
  });
  const { profileBailleurs } = useProfileBailleurs();
  const { prices } = useStripePrices(ProductType.COMPTA);
  const { initiatePayment } = useStripePayment();
  const [selectValue, setSelectValue] = useState([]);
  const [bicStatus, setBicStatus] = useState(false);
  const [immeubleStatus, setImmeubleStatus] = useState(false);
  const [ConfirmationModal, closeConfirmationModal, openConfirmationModal] =
    useModal();

  const propertiesComptaDisabled = properties.filter(
    (prop) =>
      !prop.compta_fiscal_years.includes(fiscalYear) &&
      prop.property_type === PropertyType.BIEN_COMPTABLE,
  );
  const foldersComptaDisabled = propertyFolders.filter(
    (folder) => !folder.compta_fiscal_years.includes(fiscalYear),
  );

  const selectOptions = profileBailleurs.map((profileNom) => ({
    label: profileNom.nom_profile,
    options: [
      ...propertiesComptaDisabled
        .filter((property) => property.id_profile_nom_owner === profileNom.id)
        .map((property) => ({
          domaine: BalanceDomain.PROPERTY,
          id: property.id,
        })),
      ...foldersComptaDisabled
        .filter((folder) => folder.id_profile_nom_owner === profileNom.id)
        .map((folder) => ({ domaine: BalanceDomain.FOLDER, id: folder.id })),
    ],
  }));

  const selectedPropertyIds = selectValue
    .filter((value) => value.domaine === BalanceDomain.PROPERTY)
    .map((value) => value.id);
  const selectedFolderIds = selectValue
    .filter((value) => value.domaine === BalanceDomain.FOLDER)
    .map((value) => value.id);

  const selectedNonComptableFolderNames = selectValue
    .filter((value) => value.domaine === BalanceDomain.FOLDER)
    .map((value) => propertyFoldersById[value.id])
    .filter(
      (folder) => folder?.property_type === PropertyType.BIEN_NON_COMPTABLE,
    )
    .map((folder) => folder.nom)
    .join(", ");

  const {
    comptaPriceDetails,
    isLoading: isLoadingComptePriceDetails,
    error: errorComptaPriceDetails,
  } = useStripeComptaPrice(selectedPropertyIds, selectedFolderIds, fiscalYear);

  const totalComptaPrice =
    comptaPriceDetails
      .map((price) => price.unit_amount)
      .reduce((a, b) => a + b, 0) / 100;

  async function submitHandler(e) {
    e.preventDefault();

    await initiatePayment(prices[0].id, {
      onPaymentSuccessful: () => {
        resetForm();
        onPaymentSuccessful();
        openConfirmationModal();
      },
      property_ids: selectedPropertyIds,
      folder_ids: selectedFolderIds,
      fiscal_year: fiscalYear,
    });
  }

  function resetForm() {
    setSelectValue([]);
  }

  return (
    <>
      <Modal size={"big"} onClose={resetForm}>
        <p className={"cardTitle text-center"}>
          <img
            src={`/photo_declaration_fiscale.png`}
            alt="Déclaration fiscale"
            className={"center"}
          />
          Votre déclaration fiscale
        </p>
        <p className="text-center marginB20 secondaryText">
          pour l'année {fiscalYear + 1} sur les revenus de l'année {fiscalYear}
        </p>
        <Card type={"blue"} className={"text-center marginB20"} padding={"md"}>
          <p className={"cardTitle"}>
            {COMPTA_PRICE}€ TTC par déclaration fiscale
          </p>
          <p className={"primaryBlue marginT10"}>
            149€ TTC par bien supplémentaire
          </p>
          <Button
            className={"marginT10"}
            onClick={() =>
              window.open("https://www.qalimo.fr/comptabilite-lmnp/", "_blank")
            }
          >
            En savoir plus
          </Button>
        </Card>
        <p>
          ⚠️ Vous devez effectuer une déclaration fiscale différente pour chaque
          profil bailleur.
        </p>
        <p className={"marginT5"}>
          ⚠️ Si vous êtes au revenus fonciers ou au régime micro-BIC, pas besoin
          de déclaration.{" "}
          <Button variant="tertiary" onClick={() => setBicStatus(!bicStatus)}>
            Plus d'infos
          </Button>
        </p>
        {bicStatus && (
          <Card
            type={"blue"}
            className={"text-center marginT10 marginB10"}
            padding={"md"}
          >
            Si vous êtes aux revenus fonciers ou au régime micro-BIC vous n’avez
            pas besoin de réaliser une liasse fiscale. Il vous suffit de
            déclarer le montant de vos revenus locatifs sur votre déclaration de
            revenus via le formulaire 2044 pour les revenus fonciers ou 2042
            C-PRO pour le micro-BIC.
            <Button
              className={"marginT10"}
              onClick={() =>
                window.open(
                  "https://www.qalimo.fr/declarations-simplifiees/",
                  "_blank",
                )
              }
            >
              En savoir plus
            </Button>
          </Card>
        )}
        <p className={"marginB20 marginT5"}>
          ⚠️ Si vous détenez un immeuble de rapport nous pouvons faire une
          déclaration consolidée.{" "}
          <Button
            variant="tertiary"
            onClick={() => setImmeubleStatus(!immeubleStatus)}
          >
            Plus d'infos
          </Button>
        </p>
        {immeubleStatus && (
          <Card
            type={"blue"}
            className={"text-center marginT10 marginB20"}
            padding={"md"}
          >
            <p>
              Si vous détenez un immeuble il est possible, sous certaines
              conditions, de faire une déclaration consolidée et de considérer
              l’immeuble entier comme “un seul bien”.
            </p>
            <p className={"primaryText"}>
              Dans ce cas, la déclaration fiscale sera facturée seulement au
              prix de {COMPTA_PRICE}€.
            </p>
            <p className={"marginT10 primaryText"}>
              <Check width="13" height="13" color="green" /> Pour les immeubles
              détenus par une SCI à l’impôt sur les sociétés
            </p>
            <p className={"marginT5 primaryText"}>
              <Check width="13" height="13" color="green" /> Pour les immeubles
              entièrement exploités en LMNP
            </p>
            <p className={"marginT10"}>
              Pour en bénéficier, prenez rendez-vous avec le service fiscal de
              Qalimo.
            </p>
            <Button
              className={"marginT5"}
              onClick={() =>
                window.open(
                  "https://calendly.com/christophe-df/fiscalite-avec-qalimo",
                  "_blank",
                )
              }
            >
              Prendre rendez-vous
            </Button>
          </Card>
        )}
        <form onSubmit={submitHandler}>
          <ReactSelectStandalone
            name={"properties"}
            className={"marginB20"}
            maxMenuHeight={150}
            isMulti
            mandatory
            label={
              <>
                Sélectionnez <strong>tous les biens</strong> pour lesquels une
                déclaration fiscale est à effectuer
              </>
            }
            closeMenuOnSelect={false}
            options={selectOptions}
            value={selectValue}
            getOptionValue={(option) =>
              formatBalanceAffectation(option.domaine, option.id)
            }
            getOptionLabel={(option) => {
              const { domaine, id } = option;
              if (domaine === BalanceDomain.PROPERTY)
                return propertiesById[id].nom;
              else if (domaine === BalanceDomain.FOLDER)
                return propertyFoldersById[id].nom;
            }}
            onChange={setSelectValue}
          />
          {selectedNonComptableFolderNames && (
            <Callout
              variant="warning"
              title={`⚠️ ${selectedNonComptableFolderNames}`}
            >
              <Text as="p">
                La déclaration consolidée pour un immeuble ne fonctionne que si
                le bien est détenu à 100% par la même personne.
              </Text>
              <Text as="p">
                Si l'immeuble est détenu en nom propre, il faut que
                l'intégralité des appartements soient exploités en LMNP.
              </Text>
              <Text as="p">
                Si ce n'est pas le cas, vous devez souscrire à la comptabilité
                bien par bien.
              </Text>
            </Callout>
          )}
          {comptaPriceDetails
            .filter((priceDetails) => priceDetails.unit_amount > 0)
            .map((priceDetails, i) => (
              <p key={i}>
                <span className={"primaryText"}>
                  {priceDetails.nom_profil} :
                </span>{" "}
                {currencyFormatter(priceDetails.unit_amount / 100)} pour{" "}
                {priceDetails.property_ids.length +
                  priceDetails.folder_ids.length}{" "}
                <TextOneOrMany
                  one={"bien"}
                  many={"biens"}
                  number={
                    priceDetails.property_ids.length +
                    priceDetails.folder_ids.length
                  }
                />
              </p>
            ))}
          <p className="marginT20 cardTitle text-center">
            Montant total de la déclaration :{" "}
            {currencyFormatter(totalComptaPrice)}
          </p>
          <ModalDialogFooter>
            <Button
              type={"submit"}
              isLoading={isLoadingComptePriceDetails}
              disabled={
                selectedFolderIds.length + selectedPropertyIds.length === 0 ||
                errorComptaPriceDetails
              }
            >
              Payer
            </Button>
          </ModalDialogFooter>
        </form>
      </Modal>
      <ConfirmationModal size={"medium"}>
        <div className={"text-center"}>
          <Check width="50" height="50" color="green" />
          <p className={"cardTitle marginT20"}>Paiement confirmé</p>
          <p className={"marginT10"}>
            Votre souscription à l'offre fiscale a bien été enregistrée.
          </p>
          <p className={"marginT10"}>
            Vous allez recevoir un e-mail avec toutes les informations
            concernant votre déclaration fiscale.
          </p>
          <Button className={"marginT20"} onClick={closeConfirmationModal}>
            Fermer
          </Button>
        </div>
      </ConfirmationModal>
    </>
  );
}
